import { apiClient } from '@/services/api';
import type { CampaignCopy, GiftClaim, GiftProduct, GiftShippingAddress } from '@/types/gifting';
import type { User } from '@/types/user';

export interface AthleteProfilePayload {
  first_name: string;
  last_name: string;
  email: string;
  school: string;
  sport: string;
  instagram_handle: string;
  tiktok_handle?: string;
}

export const giftingService = {
  /**
   * Public catalog. Dark (empty array) until the campaign is configured;
   * an authenticated allowlisted caller gets the list pre-launch, so send
   * auth when we have it and skip only the 401-on-missing behavior.
   */
  async fetchProducts(): Promise<{
    products: GiftProduct[];
    campaignCopy: Partial<CampaignCopy> | null;
  }> {
    const { gift_products, campaign_copy } = await apiClient.get<{
      gift_products: GiftProduct[];
      campaign_copy?: Partial<CampaignCopy> | null;
    }>('/api/v1/public/gift_products');
    return { products: gift_products, campaignCopy: campaign_copy ?? null };
  },

  /**
   * Claim one product. Server enforces the flag (403 gifting_disabled),
   * inventory (409 sold_out), and one-per-user-per-campaign (409
   * already_claimed). Retrying the same product is idempotent server-side,
   * so a double-tap or a retry after a timeout is safe.
   */
  async createClaim(giftProductId: number, shipping: GiftShippingAddress): Promise<GiftClaim> {
    const { gift_claim } = await apiClient.post<{ gift_claim: GiftClaim }>('/api/v1/gift_claims', {
      gift_product_id: giftProductId,
      shipping,
    });
    return gift_claim;
  },

  /**
   * Finish the campaign profile for the signed-in account. Contact rules are
   * the server's: email fills only when the account has none (409
   * `email_taken` → sign in with that email instead), and phone is never
   * writable here at all — an account without one is sent back to the phone
   * door (422 `phone_required`), since this endpoint can't prove possession
   * of a number typed into a form.
   */
  async completeAthleteProfile(payload: AthleteProfilePayload): Promise<User> {
    const { user } = await apiClient.patch<{ user: User }>(
      '/api/v1/gifting/athlete_profile',
      payload,
    );
    return user;
  },
};
